import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton'
import * as dashboardApi from '../services/platformDashboardService'
import type { PlatformDashboardOverview } from '../services/platformDashboardService'
import { OrgStatusBadge } from '../utils/orgStatus'

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

function StatCard({
  label,
  value,
  hint,
  to,
}: {
  label: string
  value: number | string
  hint?: string
  to?: string
}) {
  const inner = (
    <Card className={`p-5 ${to ? 'transition-shadow hover:shadow-[var(--shadow-soft)]' : ''}`}>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </Card>
  )
  if (to) {
    return (
      <Link to={to} className="block rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        {inner}
      </Link>
    )
  }
  return inner
}

export default function DashboardHome() {
  const [data, setData] = useState<PlatformDashboardOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const overview = await dashboardApi.fetchPlatformDashboardOverview()
      setData(overview)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const orgs = data?.organisations
  const showSkeleton = loading && !data
  const recentDays = data?.recentWindowDays ?? 7
  const pendingInactive = orgs?.inactive ?? 0

  const orgStatCards = orgs
    ? [
        { label: 'Organisations (total)', value: orgs.total, hint: 'All tenants on the platform' },
        { label: 'Active', value: orgs.active, hint: 'Live merchants' },
        { label: 'Inactive (setup)', value: orgs.inactive, hint: 'Not yet activated for storefront' },
        { label: 'Suspended', value: orgs.suspended, hint: 'Platform-paused' },
      ]
    : []

  const opsCards = data
    ? [
        {
          label: 'Active stores',
          value: data.activeStores,
          hint: 'Stores under active organisations',
        },
        {
          label: 'Pending withdrawals',
          value: data.pendingWithdrawals,
          hint: 'Awaiting platform approval',
          to: '/withdrawals?status=pending',
        },
        {
          label: 'Newsletter subscribers',
          value: data.newsletterSubscriberTotal,
          hint: 'Active subscriptions platform-wide',
          to: '/newsletters',
        },
      ]
    : []

  return (
    <div className="space-y-8">
      {error ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {pendingInactive > 0 && !loading ? (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100">
          <span className="font-medium">{pendingInactive}</span> organisation
          {pendingInactive === 1 ? '' : 's'} in <strong>inactive (setup)</strong> — review onboarding in{' '}
          <Link to="/organizations?status=inactive" className="font-medium text-primary underline-offset-4 hover:underline">
            Organizations
          </Link>
          .
        </div>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Organisations</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {showSkeleton
            ? Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)
            : orgStatCards.map((s) => <StatCard key={s.label} {...s} />)}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Platform operations</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {showSkeleton
            ? Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)
            : opsCards.map((s) => <StatCard key={s.label} {...s} />)}
        </div>
      </section>

      <Card>
        <CardHeader
          title="Quick links"
          description="Jump to common platform operator tasks."
          action={
            <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          }
        />
        <div className="flex flex-wrap gap-2">
          <Link to="/withdrawals?status=pending">
            <Button type="button" variant="secondary" size="sm">
              Pending withdrawals
            </Button>
          </Link>
          <Link to="/organizations">
            <Button type="button" variant="secondary" size="sm">
              All organizations
            </Button>
          </Link>
          <Link to="/organizations/new">
            <Button type="button" size="sm">
              New organization
            </Button>
          </Link>
          <Link to="/payment-gateways">
            <Button type="button" variant="secondary" size="sm">
              Payment gateways
            </Button>
          </Link>
          <Link to="/newsletters">
            <Button type="button" variant="secondary" size="sm">
              Newsletter
            </Button>
          </Link>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={`New organizations (${recentDays} days)`}
            description="Recently onboarded tenants"
          />
          {showSkeleton ? (
            <div className="space-y-3 p-1">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !data?.recentOrganisations?.length ? (
            <p className="text-sm text-muted-foreground">No new organizations in the last {recentDays} days.</p>
          ) : (
            <ul className="divide-y divide-border rounded-2xl border border-border">
              {data.recentOrganisations.map((o) => (
                <li key={o.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{o.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.slug} · {formatWhen(o.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <OrgStatusBadge status={o.status} />
                    <Link to={`/organizations/${o.id}`} className="text-sm font-medium text-primary hover:text-primary/80">
                      View →
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader
            title={`Recent newsletter signups (${recentDays} days)`}
            description="Latest subscriber activity"
          />
          {showSkeleton ? (
            <div className="space-y-3 p-1">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !data?.recentNewsletterSignups?.length ? (
            <p className="text-sm text-muted-foreground">No newsletter signups in the last {recentDays} days.</p>
          ) : (
            <ul className="divide-y divide-border rounded-2xl border border-border">
              {data.recentNewsletterSignups.map((row) => (
                <li key={row.id} className="px-4 py-3">
                  <p className="font-medium text-foreground">{row.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.storeName ?? `Store #${row.storeId}`}
                    {row.organisationName ? ` · ${row.organisationName}` : ''} · {formatWhen(row.subscribedAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
